import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-14198e33"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "floor-plan" }
const _hoisted_2 = ["src"]
const _hoisted_3 = { class: "property-listing-floor-plan-buttons" }
const _hoisted_4 = {
  key: 0,
  class: "fa-thin fa-cloud-arrow-up"
}

import { DxButton } from "devextreme-vue";
import FileUpload from "@/components/ui/inputs/file-upload.vue";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { PropertyFloorPlanTypes } from "@/enums/properties/PropertyFloorPlanTypes";
import { ref, watch, PropType, computed, inject, onMounted } from "vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { Properties } from "acquaint-api/models/Properties";
import { saveAs } from "file-saver-es";

const printFileTypes = ".jpg, .jpeg, .gif, .tif, .wmf, .png";

export default /*#__PURE__*/_defineComponent({
  __name: 'property-listing-floor-upload',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  fetchData: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for a property.
   * @type {number}
   * @required
   */
  propertyId: {
    type: Number,
    required: true,
  },
  /**
   * The property object representing the specific property being worked with.
   * @type {Properties}
   */
  propertyData: {
    type: Object as PropType<Properties>,
  },
  /**
   * Indicates whether the user has permission to edit the property details.
   * @type {boolean}
   * @required
   */
  permissionToEdit: {
    type: Boolean,
    required: true,
  },
},
  emits: [
  /**
   * Triggered when a new floor plan is added.
   * This notifies the parent component that a new plan has been uploaded successfully.
   * @property {boolean} fileSubmitted - Indicates whether the file submission was successful.
   */
  "planAdded",
  /**
   * Triggered when a floor plan is deleted.
   * This informs the parent component to update the property data accordingly.
   * @property {boolean} deletionSuccessful - Indicates whether the plan was successfully deleted.
   */
  "planDeleted",
],
  setup(__props, { emit }) {

const props = __props;





const triggerFileUploading = ref(false);
const resetFiles = ref(false);
const floorFile = ref<File[]>();
const fileSubmitted = ref(false);
const retrievedPropertyData = ref<Properties>();
const saving = ref(false);
const floorPlanFile = ref<Blob>();
const floorPlanUrl = ref("");
const isWebPlanPdf = ref(false);
const windowUrl = computed(() => window.webkitURL || window.URL);
const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;

watch(
  () => props.fetchData,
  () => {
    if (!props.fetchData) {
      return;
    }
    saving.value = false;
    floorFile.value = [];
    fileSubmitted.value = true;
    fetchPropertyData();
    fetchPlans();
  },
);

onMounted(() => {
  saving.value = false;
  floorFile.value = [];
  fileSubmitted.value = true;
  fetchPropertyData();
  fetchPlans();
});

const fetchPropertyData = async () => {
  try {
    retrievedPropertyData.value = await apiClient.httpGet(
      `database/properties/${props.propertyId}`,
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    retrievedPropertyData.value = undefined;
  }
};

const uploadPrintPlan = (e: File[]) => {
  uploadAddedImage(e);
};

const uploadAddedImage = async (e: File[]) => {
  if (!e || (e && e.length == 0)) {
    return;
  }

  triggerFileUploading.value = false;

  // This tells the underlying upload component to clear previously selected files so they don't get re-uloaded
  resetFiles.value = true;

  if (
    !(await submitFloorPlansToAPI(e[0], PropertyFloorPlanTypes.Print)) ||
    !(await submitFloorPlansToAPI(e[0], PropertyFloorPlanTypes.Web))
  ) {
    resetFiles.value = false;
    return;
  }

  resetFiles.value = false;
  fileSubmitted.value = true;
  await fetchPropertyData();
  floorFile.value = e;
  displayWebFloorPlan();
  emit("planAdded", fileSubmitted, retrievedPropertyData.value);
};

const submitFloorPlansToAPI = async (
  image: File,
  planType: PropertyFloorPlanTypes,
) => {
  saving.value = true;
  try {
    await apiClient.uploadFile(
      `app/properties/${props.propertyId}/floorplan/${planType}`,
      image,
    );
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  } finally {
    saving.value = false;
  }
};

const deletePlan = async () => {
  let editablePorpertyData: Properties = JSON.parse(
    JSON.stringify(retrievedPropertyData.value),
  );
  let deletionSuccessful = true;

  editablePorpertyData.floorPlanFileNameWeb = "";
  editablePorpertyData.floorPlanFileNamePrint = "";

  try {
    editablePorpertyData = await apiClient.httpPut(
      `database/properties/${props.propertyId}`,
      editablePorpertyData,
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    deletionSuccessful = false;
  }

  if (!deletionSuccessful) {
    return;
  }

  floorPlanUrl.value = "";
  floorFile.value = [];

  await fetchPropertyData();
  emit("planDeleted", true, retrievedPropertyData.value);
};

const resetFloorPlanVariables = () => {
  revokeFloorPlanUrls();
  floorPlanFile.value = undefined;
  floorPlanUrl.value = "";
  isWebPlanPdf.value = false;
};

const fetchPlans = async () => {
  // We should reset this to ensure the wrong images aren't displayed.
  resetFloorPlanVariables();

  if (!props.propertyData) {
    return;
  }

  if (props.propertyData.floorPlanFileNamePrint) {
    await displayPrintFloorPan();
  } else if (props.propertyData.floorPlanFileNameWeb) {
    await displayWebFloorPlan();
  }
};

const displayPrintFloorPan = async () => {
  const blob = await fetchFloorPlan(PropertyFloorPlanTypes.Print);

  if (blob != undefined) {
    floorPlanUrl.value = windowUrl.value.createObjectURL(blob);
  } else {
    floorPlanUrl.value = "";
  }
};

const displayWebFloorPlan = async () => {
  floorPlanFile.value = await fetchFloorPlan(PropertyFloorPlanTypes.Web);

  if (!floorPlanFile.value) {
    floorPlanUrl.value = "";
    return;
  }

  isWebPlanPdf.value = floorPlanFile.value.type === "application/pdf";

  if (!isWebPlanPdf.value) {
    floorPlanUrl.value = windowUrl.value.createObjectURL(floorPlanFile.value);
  }
};

const fetchFloorPlan = async (type: PropertyFloorPlanTypes) => {
  try {
    return await apiClient.getFile(
      `app/properties/${props.propertyData?.id}/floorplan?type=${type}`,
    );
  } catch (err) {
    return undefined;
  }
};

const onWebPdfDownloadClick = () => {
  if (!floorPlanFile.value) {
    return;
  }

  saveAs(
    floorPlanFile.value,
    `floorplan-for-web-${props.propertyData?.id}.pdf`,
  );
};

const revokeFloorPlanUrls = () => {
  if (floorPlanUrl.value != "") {
    windowUrl.value.revokeObjectURL(floorPlanUrl.value);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      'edit-floor-plan-container',
      { 'no-floor-plan-selected': !floorPlanUrl.value },
    ])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (
          retrievedPropertyData.value?.floorPlanFileNameWeb &&
          floorPlanUrl.value &&
          !isWebPlanPdf.value
        )
        ? (_openBlock(), _createElementBlock("img", {
            key: 0,
            src: floorPlanUrl.value
          }, null, 8, _hoisted_2))
        : (floorPlanFile.value && isWebPlanPdf.value)
          ? (_openBlock(), _createBlock(_unref(DxButton), {
              key: 1,
              onClick: onWebPdfDownloadClick,
              text: "Download Floor Plan",
              icon: "fa-sharp fa-light fa-file-pdf",
              width: "200px"
            }))
          : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_3, [
      (!floorPlanUrl.value)
        ? (_openBlock(), _createElementBlock("i", _hoisted_4))
        : _createCommentVNode("", true),
      _createVNode(FileUpload, {
        text: "Upload Plan",
        onFilesChanged: uploadPrintPlan,
        showFileList: false,
        readOnly: false,
        buttonBackground: "#0070d2",
        buttonTextColor: "white",
        triggerFileUploading: triggerFileUploading.value,
        reset: resetFiles.value,
        multiple: false,
        onUploadCancelled: _cache[0] || (_cache[0] = ($event: any) => (triggerFileUploading.value = false)),
        width: "130px",
        "file-types": printFileTypes
      }, null, 8, ["triggerFileUploading", "reset"]),
      (
          retrievedPropertyData.value?.floorPlanFileNamePrint != '' ||
          retrievedPropertyData.value?.floorPlanFileNameWeb != ''
        )
        ? (_openBlock(), _createBlock(_unref(DxButton), {
            key: 1,
            id: "delete-floor-plan-button",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (deletePlan())),
            text: "Delete Plan",
            width: "130px"
          }))
        : _createCommentVNode("", true)
    ])
  ], 2))
}
}

})